import React, {  useState, useEffect} from 'react';
import { useComponentLogger } from '@/lib/component-logger';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { dayjs } from '@/lib/dayjs';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { MenuItem } from '@mui/material';
import Select from '@mui/material/Select';
import { spanDisplayNames, dateRangeRegex } from '@/lib/dataTypes/span';
import Stack from '@mui/material/Stack';

/**
 * Select text, if range is selected, then appends a range selector
 * Only handles UI, state changes + custom side effects are handled by the parent
 * Current top level pages that use this:
 * - Historian Page: static page
 * - View Page: dynamic page, uses SelectDateTextWrapper to handle state
 * @param {*} param
 */
export function SelectDateText({config = {}, value={value: null, dis: null, default: null}, handleSelectParent, disabled, setDisabled = () => console.warn("setDisabled not passed!")}) {
    const logger = useComponentLogger('Select Date Text');
    // const [selectedOption, setSelectedOption] = useState({value: null, dis: null, default: param?.default || null});
    // const [disabled, setDisabled] = useState(true);
    const [showRangePicker, setShowRangePicker] = useState(false);
    const [startDate, setStartDate] = useState(null);
    const [startMax, setStartMax] = useState(dayjs()); // max start is today
    const [endDate, setEndDate] = useState(null);
    const [endMin, setEndMin] = useState(null);

    /**
     * Updates selectedViewParams when a new selection is made
     * Clears any child params this param may have
     * @param {*} event 
     */
    const handleSelect = (event) => {
        logger.debug('new select', event.target);

        let selectData = event.target.value;
        let selectName = event.target.name;

        if (selectData.value === "range") {
            setShowRangePicker(true);
        } else {
            setShowRangePicker(false);
            setStartDate(null);
            setEndDate(null);
            setStartMax(null);
            setEndMin(null);

        }

        handleSelectParent(event.target.value); // parent handles any global state updates/custom behaviour
    }

    /**
     * Used to generate each option in the dropdown list
     * @param {*} option 
     * @returns 
     */
    const renderOption = (option) => {
        // logger.debug("cur option", option);
        let val = option.value || option.id?.val || option;
        let dis = '';

        if (option.dis) {
            dis = option.dis;
        } else if (option.id && option.id.dis) {
            dis = option.id.dis;
        } else if (spanDisplayNames[option]) {
            dis = spanDisplayNames[option];
        } else if (typeof(option) === 'string') {
            dis = option;
        }

        let optionValue = {
            value: val,
            dis: dis,
            default: config.default ?? null,
            name: config.name ?? null,
            parentName: config.parentName || null,
            kind: config.kind ?? null,
            type: config.type ?? null
        }

        // logger.debug('final option', optionValue);

        return <MenuItem value={optionValue} key={`menu-item-${val}`}>{dis}</MenuItem>

    }

    const handleStartChange = (value) => {
        // let newStart = dayjs(value['$d']).format('YYYY-MM-DD');
        setEndMin(value);
        setStartDate(value);
    }

    const handleEndChange = (value) => {
        // let newEnd = dayjs(value['$d']).format('YYYY-MM-DD');
        setStartMax(value);
        setEndDate(value);
    }

    const handleStartError = (value) => {
        setStartDate(null);
        setEndMin(null);
    }

    const handleEndError = (value) => {
        setEndDate(null);
        setStartMax(null);
    }

    /**
     * When both a start and end date is selected, parse them into the format 'startDate..endDate' and update the selectedViewParams
     */
    useEffect(() => {
        logger.debug('new start, end', startDate, endDate);
        if (startDate && endDate ) {
            let newStart = dayjs(startDate['$d'])?.format('YYYY-MM-DD');
            let newEnd = dayjs(endDate['$d'])?.format('YYYY-MM-DD');
            let newDate = `${newStart}..${newEnd}`;

            let optionValue = {
                value: newDate,
                dis: "Range",
                default: config.default ?? null,
                name: config.name ?? null,
                parentName: config.parentName ?? null,
                kind: config.kind ?? null,
                type: config.type ?? null
            }

            handleSelectParent(optionValue)

        } 
    }, [startDate, endDate]);

    useEffect(() => {
        logger.debug("value change", value, config);

        // determine if we should render a range based on the initial value
        if (config.type === "dateSpan" && value?.value?.match(dateRangeRegex)) {
            let dates = value.value?.split('..');
            let start = dayjs(dates[0]);
            let end = dayjs(dates[1]);
            handleStartChange(start);
            handleEndChange(end);

            setShowRangePicker(true);
        }
    }, [config]);

    useEffect(() => {
        logger.debug('disabled change', value, setDisabled, disabled)
        if (!value || !value.dis) {
            if (config.options?.length > 0) {
                setDisabled(false);
            } else {
                setDisabled(true);
            }
        } else {
            setDisabled(false);
        }
    }, [value, config.options, setDisabled]);


    return (
        <>
        <FormControl sx={{width: '100%'}}>
            <InputLabel id="select-label">{config.dis || ''}</InputLabel>
            <Select 
                labelId="select-label" 
                id="select" 
                value={value}
                onChange={handleSelect} 
                disabled={disabled}
                name={config.name || ""}
                displayEmpty 
                renderValue={(value) => {
                    logger.debug('render select', value, config);
                    if (!value || !value.dis) {
                        if (config.options?.length > 0) {
                            return <p style={{margin: 0}}>{`Select ${config.dis}`}</p>;
                        } else {
                            return <p style={{margin: 0}}>{`No options available`}</p>
                        }
                    } else {
                        return value.dis;
                    }
                }} 
            >
                {config?.options?.map((option) => (renderOption(option)))}
            </Select>
        </FormControl>
        {showRangePicker ? 
            <Stack direction="row" spacing={3} sx={{ alignItems: 'center', justifyContent: 'center' }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker value={startDate} format="YYYY-MM-DD"  maxDate={startMax} onChange={handleStartChange}  />
                    -
                    <DatePicker value={endDate} format="YYYY-MM-DD"  minDate={endMin} maxDate={dayjs()} onChange={handleEndChange}  />
                    {/* If we wanna allow the value to be cleared (only available on desktop) */}
                    {/* <DatePicker format="YYYY-MM-DD" value={endDate} minDate={endMin} maxDate={dayjs()} onChange={handleEndChange} onError={handleEndError} slotProps={{ field: {clearable: true, onClear: () => setCleared(true)}}} /> */}
                </LocalizationProvider> 
            </Stack>
                : <></>
        }
        </>      
    );
    
}